import { useAuthStore } from '@/stores/authStore';
/**
 * SSL Wide Watch API client. The SB3 backend serves all endpoints under
 * `/sslwidewatch/**` and expects a bearer token in the `Authorization`
 * header on every authenticated call.
 *
 * Auth lifecycle (V016):
 *   * Signin returns BOTH an access JWT (15 min) and a refresh token
 *     (30 days). Both are stashed in the zustand auth store.
 *   * Every authenticated request sends the access JWT in the
 *     Authorization header.
 *   * When a request 401s, this client transparently calls
 *     POST /sslwidewatch/auth/refresh, swaps in the new token pair,
 *     and retries the original request once. The user never sees the
 *     hop unless their refresh token has also expired (30 days idle)
 *     or been revoked, in which case clearAuth() fires and the
 *     <RequireAuth /> guard bounces them to /signin.
 *   * Concurrency: many in-flight requests can 401 at the same time
 *     (e.g. on first page load after the access token expires). All
 *     of them await the same refreshPromise — only ONE refresh request
 *     ever fires per access-token expiry.
 */
/**
 * Where to send API requests.
 *
 * V016 (refresh tokens in localStorage) hit the backend cross-origin
 * at {@code https://staging.ssl.wide.watch}.
 *
 * V020 (HttpOnly refresh cookies) routes through the same-origin
 * Pages Function proxy at {@code /api/*} (functions/api/[[path]].ts).
 * That collapses everything to one origin so HttpOnly cookies on
 * .one.wide.watch are seen on every request automatically.
 *
 * The default is the proxy. Override via {@code VITE_API_BASE_URL}
 * for local dev (e.g. {@code http://localhost:8080}) or for the
 * legacy SSLWideWatchUI bundle which can't run a Pages Function.
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';
export class ApiError extends Error {
    status;
    code;
    constructor(message, status, code) {
        super(message);
        this.status = status;
        this.code = code;
    }
    get is4xx() {
        return this.status >= 400 && this.status < 500;
    }
    get is5xx() {
        return this.status >= 500;
    }
    get isTransient() {
        return this.is5xx || this.status === 429;
    }
}
function buildUrl(path, query) {
    const base = API_BASE_URL.replace(/\/$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const url = `${base}${normalizedPath}`;
    if (!query)
        return url;
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
        if (v === undefined || v === null)
            continue;
        params.set(k, String(v));
    }
    const qs = params.toString();
    return qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url;
}
/**
 * Singleton refresh promise. Concurrent 401s all await the SAME
 * promise so we never fire two /auth/refresh calls in parallel —
 * which would burn one of the two refresh tokens (rotation makes
 * each single-use, and the second one would trigger the backend's
 * reuse-detection and revoke the family).
 *
 * Set when a refresh starts; cleared in `finally` so the next
 * legitimate expiry can start a fresh refresh.
 */
let refreshPromise = null;
async function attemptRefresh() {
    // Coalesce — every concurrent 401 awaits the same promise.
    if (refreshPromise)
        return refreshPromise;
    const refreshToken = useAuthStore.getState().refreshToken;
    refreshPromise = (async () => {
        try {
            const url = buildUrl('/sslwidewatch/auth/refresh');
            // credentials: 'include' so the HttpOnly wwrt cookie attaches.
            // Body fallback for the legacy code path that still keeps the
            // refresh token in localStorage. Once the cookie path is the
            // only path (legacy UI retired, all clients on V020+), the
            // body field can go away.
            const res = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify(refreshToken ? { refreshToken } : {}),
            });
            if (!res.ok)
                return false;
            const payload = (await res.json());
            if (!payload.accessToken)
                return false;
            useAuthStore.getState().setTokens({
                accessToken: payload.accessToken,
                // refreshToken in the body is the back-compat field; the
                // cookie is the source of truth on V020+. We still mirror
                // it into the store so signOut() can include it as a
                // belt-and-suspenders body field.
                refreshToken: payload.refreshToken ?? refreshToken ?? null,
            });
            return true;
        }
        catch {
            return false;
        }
        finally {
            refreshPromise = null;
        }
    })();
    return refreshPromise;
}
async function request(method, path, body, opts = {}, isRetry = false) {
    const url = buildUrl(path, opts.query);
    const headers = new Headers({ Accept: opts.accept ?? 'application/json' });
    if (!opts.anonymous) {
        const token = useAuthStore.getState().accessToken;
        if (token)
            headers.set('Authorization', `Bearer ${token}`);
    }
    let initBody;
    if (opts.rawBody !== undefined) {
        initBody = opts.rawBody;
    }
    else if (body !== undefined) {
        headers.set('Content-Type', 'application/json');
        initBody = JSON.stringify(body);
    }
    // credentials: 'include' so the wwrt HttpOnly refresh cookie
    // attaches on auth endpoints. Bearer JWT still flows in the
    // Authorization header for everything that needs it.
    const res = await fetch(url, { method, headers, body: initBody, credentials: 'include' });
    // 401 handling — try a refresh exactly once, then retry the original
    // request. If the refresh itself fails (or this IS the retry), we
    // give up and clear auth so the route guard pushes to /signin.
    if (res.status === 401 && !opts.anonymous && !isRetry) {
        const refreshed = await attemptRefresh();
        if (refreshed) {
            return request(method, path, body, opts, /*isRetry*/ true);
        }
        useAuthStore.getState().clearAuth();
        throw new ApiError('Not authenticated', 401);
    }
    if (res.status === 401 && !opts.anonymous && isRetry) {
        // Refresh succeeded but the next call still 401'd — token must
        // have been revoked between the two. Treat as logged out.
        useAuthStore.getState().clearAuth();
        throw new ApiError('Not authenticated', 401);
    }
    if (!res.ok) {
        let code;
        let message = res.statusText || 'Request failed';
        try {
            const payload = (await res.clone().json());
            code = payload.error?.code;
            message = payload.error?.message ?? payload.message ?? message;
        }
        catch {
            /* non-JSON body */
        }
        throw new ApiError(message, res.status, code);
    }
    if (res.status === 204)
        return undefined;
    // Some endpoints (e.g. POST /sslwidewatch/auth/forgot-password)
    // return 200 with `Content-Type: application/json` but an empty
    // body (Content-Length: 0). Calling res.json() on an empty stream
    // throws `SyntaxError: Unexpected end of JSON input`, which the
    // callers see as "Request failed." Gowtham hit this on the forgot-
    // password page (2026-05-25). Treat content-length=0 as a no-
    // content success regardless of advertised content-type.
    if (res.headers.get('content-length') === '0')
        return undefined;
    const contentType = res.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
        return (await res.text());
    }
    // Belt-and-braces: even with no content-length header, an empty
    // body would still trip res.json(). Read as text first, then parse.
    const raw = await res.text();
    if (raw.length === 0)
        return undefined;
    return JSON.parse(raw);
}
export const apiClient = {
    get: (path, opts) => request('GET', path, undefined, opts),
    post: (path, body, opts) => request('POST', path, body, opts),
    put: (path, body, opts) => request('PUT', path, body, opts),
    patch: (path, body, opts) => request('PATCH', path, body, opts),
    delete: (path, opts) => request('DELETE', path, undefined, opts),
};
/**
 * Sign the current user out: revoke the refresh-token family on the
 * backend (so the token can't be reused even if it leaks before
 * localStorage clears), then nuke local auth. Wire this to the
 * sign-out menu item when one exists.
 *
 * Idempotent — safe to call from a route guard or a button.
 */
export async function signOut() {
    const refreshToken = useAuthStore.getState().refreshToken;
    try {
        await fetch(buildUrl('/sslwidewatch/auth/logout'), {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
            // Best-effort: don't block sign-out on network failure
        });
    }
    catch { /* swallow — local clear still happens */ }
    useAuthStore.getState().clearAuth();
}
/**
 * Sign out of EVERY device. Backend revokes every refresh-token family
 * for this user (so the phone, the laptop, that browser left logged in
 * at the office — all of them). Other devices will see a 401 on their
 * next API call, fail to refresh, and bounce to /login.
 */
export async function signOutEverywhere() {
    const refreshToken = useAuthStore.getState().refreshToken;
    try {
        await fetch(buildUrl('/sslwidewatch/auth/logout'), {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken, everywhere: true }),
        });
    }
    catch { /* swallow — local clear still happens */ }
    useAuthStore.getState().clearAuth();
}
